import type { TileDef, TileMap } from '@archon3d/core';
import type { Material } from '@babylonjs/core/Materials/material';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import {
  ALL_SIDES,
  EAST,
  NORTH,
  SOUTH,
  WEST,
  cellFor,
  cellUv,
  edgeScheme,
  familyOf,
} from './atlasLayout';

/** Tiles per chunk along x and z; a chunk spans the arena's full height. */
export const CHUNK = 8;
/** Where a pool's floor lies: a little below the walking plane, so ponds and streams read as sunken. */
export const POOL_FLOOR = -0.15;
/**
 * The ground slab sits one metre below the walking plane, so its top face *is* that plane at
 * y = 0 and an obstacle's own blocks stack up from there.
 */
const GROUND_Y = -1;
/** Vertex brightness for ambient occlusion levels 0 (fully occluded) … 3 (open). */
const AO_LEVELS = [0.5, 0.68, 0.85, 1] as const;

type Axis = 0 | 1 | 2;

/** The materials a tile family may name that are drawn as a liquid's surface (`liquidSurfaces.ts`). */
export const LIQUID_MATERIALS: readonly string[] = ['water', 'lava'];

/**
 * How a tile is drawn:
 * - `floor`: flat ground at the walking plane;
 * - `pool`: a sunken floor, for a liquid, or a family naming a liquid's material that nothing may
 *   enter (lava closed to walkers is a pool, not a wall); lava a walker crosses is `floor`;
 * - `block`: an obstacle standing `top` metres high, its true height;
 * - `prop`: an obstacle drawn as a model standing on bare ground (`arenaProps.ts`): trees.
 *
 * A flat family that nothing may enter and that names no liquid stands a metre high, so it reads
 * as closed.
 */
export interface TileShape {
  kind: 'floor' | 'pool' | 'block' | 'prop';
  top: number;
}

export function tileShape(tile: TileDef): TileShape {
  // A liquid, or a liquid's material that nothing may enter, is sunken; lava a walker crosses
  // (D-43) lies flat on the walking plane, its surface drawn over the ground's own top.
  if (
    tile.walk === 'liquid' ||
    (LIQUID_MATERIALS.includes(tile.material ?? '') && tile.walk === 'blocked')
  )
    return { kind: 'pool', top: POOL_FLOOR };
  if (tile.walk !== 'blocked') return { kind: 'floor', top: 0 };
  const top = tile.height > 0 ? tile.height : 1;
  return { kind: tile.autotile === 'scatter' ? 'prop' : 'block', top };
}

/**
 * One face direction. Corners are listed in Babylon's front-face order (clockwise seen from
 * outside in its left-handed space); `u` and `v` are the axes the corner offsets span.
 */
interface FaceDef {
  normal: [number, number, number];
  u: Axis;
  v: Axis;
  /** Whether the u or the v axis runs along world y (tile images must stand upright). */
  upright: 'u' | 'v' | 'none';
  corners: [number, number, number][];
  side: 'top' | 'bottom' | 'side';
}

const FACES: FaceDef[] = [
  {
    normal: [0, 1, 0],
    u: 0,
    v: 2,
    upright: 'none',
    corners: [
      [0, 1, 0],
      [1, 1, 0],
      [1, 1, 1],
      [0, 1, 1],
    ],
    side: 'top',
  },
  {
    normal: [0, -1, 0],
    u: 2,
    v: 0,
    upright: 'none',
    corners: [
      [0, 0, 0],
      [0, 0, 1],
      [1, 0, 1],
      [1, 0, 0],
    ],
    side: 'bottom',
  },
  {
    normal: [1, 0, 0],
    u: 2,
    v: 1,
    upright: 'v',
    corners: [
      [1, 0, 0],
      [1, 0, 1],
      [1, 1, 1],
      [1, 1, 0],
    ],
    side: 'side',
  },
  {
    normal: [-1, 0, 0],
    u: 1,
    v: 2,
    upright: 'u',
    corners: [
      [0, 0, 0],
      [0, 1, 0],
      [0, 1, 1],
      [0, 0, 1],
    ],
    side: 'side',
  },
  {
    normal: [0, 0, 1],
    u: 1,
    v: 0,
    upright: 'u',
    corners: [
      [0, 0, 1],
      [0, 1, 1],
      [1, 1, 1],
      [1, 0, 1],
    ],
    side: 'side',
  },
  {
    normal: [0, 0, -1],
    u: 0,
    v: 1,
    upright: 'v',
    corners: [
      [0, 0, 0],
      [1, 0, 0],
      [1, 1, 0],
      [0, 1, 0],
    ],
    side: 'side',
  },
];

/**
 * Builds one mesh per chunk of the tilemap: a ground slab under every tile and a box of the
 * family's own height over the blocked ones, emitting only the faces that touch open air, with
 * per-vertex ambient occlusion in the colour channel and atlas UVs per face.
 *
 * Obstacles stand at their true heights: the duel's rules read them (a flier crosses anything
 * under two metres, and a shot or a look passes over a tile lower than it), and rounded up to
 * whole metres a 1.2 m rock was drawn as a 2 m wall that shots flew through. Trees are drawn as
 * trees by `arenaProps.ts`, over bare ground here, and liquids get their surfaces from
 * `liquidSurfaces.ts` over the sunken floor here.
 *
 * The chunks never move, but their parent does, between the table and true size: whoever places
 * the parent freezes their world matrices after it (the arena stage does), not this.
 */
export function buildArenaMeshes(
  scene: Scene,
  map: TileMap,
  material: Material,
  parent: TransformNode,
): Mesh[] {
  const meshes: Mesh[] = [];
  for (let cz = 0; cz < map.height; cz += CHUNK) {
    for (let cx = 0; cx < map.width; cx += CHUNK) {
      const mesh = buildChunk(scene, map, cx, cz, material);
      if (!mesh) continue;
      mesh.parent = parent;
      meshes.push(mesh);
    }
  }
  return meshes;
}

function buildChunk(
  scene: Scene,
  map: TileMap,
  cx: number,
  cz: number,
  material: Material,
): Mesh | null {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const shapeOf = (x: number, z: number) => tileShape(map.tile(x, z));
  /** Blocks stacked over the walking plane, the last one cut to the obstacle's height. */
  const blocksHigh = (x: number, z: number) => {
    const shape = shapeOf(x, z);
    return shape.kind === 'block' ? Math.ceil(shape.top) : 0;
  };
  // Outside the arena counts as solid at every height, so the rim needs no faces of its own.
  const solid = (x: number, y: number, z: number) =>
    !map.inside(x, z) || y <= GROUND_Y || y < blocksHigh(x, z);
  /** The top of a solid cell: its layer's, cut to the obstacle's height, or a pool's floor. */
  const topOf = (x: number, y: number, z: number) => {
    const shape = shapeOf(x, z);
    if (y === GROUND_Y) return shape.kind === 'pool' ? POOL_FLOOR : 0;
    return Math.min(y + 1, shape.top);
  };
  /** How high a neighbour's solid reaches within layer `y`; its bottom when it has none there. */
  const coverOf = (x: number, y: number, z: number) => {
    if (!map.inside(x, z)) return Infinity;
    return solid(x, y, z) ? topOf(x, y, z) : y;
  };

  let tallest = 0;
  for (let z = cz; z < Math.min(cz + CHUNK, map.height); z++)
    for (let x = cx; x < Math.min(cx + CHUNK, map.width); x++)
      tallest = Math.max(tallest, blocksHigh(x, z));

  for (let y = GROUND_Y; y < tallest; y++) {
    for (let z = cz; z < Math.min(cz + CHUNK, map.height); z++) {
      for (let x = cx; x < Math.min(cx + CHUNK, map.width); x++) {
        if (!solid(x, y, z)) continue;
        const tile = map.tile(x, z);
        const shape = shapeOf(x, z);
        // Only the ground slab of a pool is drawn sunken; it is what makes a stream read.
        const liquid = y === GROUND_Y && shape.kind === 'pool';
        // Under a tree lies bare forest floor, not the tree's own bark and leaves.
        const family = shape.kind === 'prop' ? 'tree' : tile.id;
        const top = topOf(x, y, z);
        for (const face of FACES) {
          const [nx, ny, nz] = face.normal;
          if (ny !== 0) {
            if (!solid(x, y + ny, z)) emitFace(x, y, z, face, family, liquid, y, top);
            continue;
          }
          // A side shows wherever its neighbour stands lower than it: the step down into a pool,
          // or the part of a wall above a shorter one.
          const cover = coverOf(x + nx, y, z + nz);
          if (cover < top) emitFace(x, y, z, face, family, liquid, Math.max(y, cover), top);
        }
        // A castle's wall top carries battlements along every side it faces out on.
        if (familyOf(family).crenels && shape.kind === 'block' && y === Math.ceil(shape.top) - 1)
          emitCrenels(x, z, family, shape.top);
      }
    }
  }
  if (indices.length === 0) return null;

  const mesh = new Mesh(`chunk-${cx}-${cz}`, scene);
  const data = new VertexData();
  data.positions = positions;
  data.normals = normals;
  data.uvs = uvs;
  data.colors = colors;
  data.indices = indices;
  data.applyToMesh(mesh, false);
  mesh.material = material;
  mesh.isPickable = false;
  return mesh;

  function emitFace(
    x: number,
    y: number,
    z: number,
    face: FaceDef,
    family: string,
    liquid: boolean,
    bottom: number,
    top: number,
  ) {
    const cell =
      face.side === 'top'
        ? cellFor(family, 'top', joinedSides(map, x, z, family))
        : cellFor(family, face.side);
    const uv = cellUv(cell);
    // Each tile a shade lighter or darker than the next, so a field of one picture does not read
    // as a grid of it.
    const shade = liquid ? 1 : tileShade(x, z);
    const [nx, ny, nz] = face.normal;
    const base = positions.length / 3;
    const ao: number[] = [];
    face.corners.forEach((corner) => {
      const cu = corner[face.u];
      const cv = corner[face.v];
      // Side blocks and the diagonal block next to this corner, in the plane in front of the face.
      const su = cu ? 1 : -1;
      const sv = cv ? 1 : -1;
      const offset = (du: number, dv: number): [number, number, number] => {
        const o: [number, number, number] = [x + nx, y + ny, z + nz];
        o[face.u] += du;
        o[face.v] += dv;
        return o;
      };
      const side1 = solid(...offset(su, 0)) ? 1 : 0;
      const side2 = solid(...offset(0, sv)) ? 1 : 0;
      const diagonal = solid(...offset(su, sv)) ? 1 : 0;
      const level = side1 && side2 ? 0 : 3 - (side1 + side2 + diagonal);
      ao.push(level);

      const height = corner[1] === 1 ? top : bottom;
      positions.push(x + corner[0], height, z + corner[2]);
      normals.push(nx, ny, nz);
      const across = face.upright === 'u' ? cv : cu;
      // An upright face cut short shows as much of its tile image as it is tall, not a squashed
      // whole one.
      const along = face.upright === 'none' ? (cv ? 1 : 0) : Math.min(1, Math.max(0, height - y));
      uvs.push(across ? uv.u1 : uv.u0, uv.v0 + (uv.v1 - uv.v0) * along);
      const bright = liquid ? 1 : AO_LEVELS[level]! * shade;
      colors.push(bright, bright, bright, 1);
    });
    // Split the quad along the diagonal that keeps the occlusion gradient smooth.
    if (ao[0]! + ao[2]! > ao[1]! + ao[3]!)
      indices.push(base + 1, base + 2, base + 3, base + 1, base + 3, base);
    else indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  /**
   * The battlements of a castle: a merlon in the middle of each side of the wall's top that faces
   * out, and a post on each corner where two such sides meet, so that none overlaps another.
   */
  function emitCrenels(x: number, z: number, family: string, top: number) {
    const joined = joinedSides(map, x, z, family);
    const uv = cellUv(cellFor(family, 'side'));
    const shade = tileShade(x, z) * AO_LEVELS[2]!;
    const open = (bit: number) => !(joined & bit);
    const lo = 0.5 - MERLON_WIDTH / 2;
    const hi = 0.5 + MERLON_WIDTH / 2;
    const d = MERLON_DEPTH;
    const box = (x0: number, x1: number, z0: number, z1: number) =>
      emitBox(x + x0, top, z + z0, x + x1, top + MERLON_HEIGHT, z + z1, uv, shade);
    if (open(NORTH)) box(lo, hi, 1 - d, 1);
    if (open(SOUTH)) box(lo, hi, 0, d);
    if (open(EAST)) box(1 - d, 1, lo, hi);
    if (open(WEST)) box(0, d, lo, hi);
    if (open(NORTH) && open(EAST)) box(1 - d, 1, 1 - d, 1);
    if (open(NORTH) && open(WEST)) box(0, d, 1 - d, 1);
    if (open(SOUTH) && open(EAST)) box(1 - d, 1, 0, d);
    if (open(SOUTH) && open(WEST)) box(0, d, 0, d);
  }

  /** A box's top and four sides (its bottom stands on the wall), in the chunk's material. */
  function emitBox(
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
    uv: { u0: number; u1: number; v0: number; v1: number },
    shade: number,
  ) {
    const quad = (
      corners: [number, number, number][],
      normal: [number, number, number],
      spanU: number,
      spanV: number,
    ) => {
      const base = positions.length / 3;
      const us = [0, spanU, spanU, 0];
      const vs = [0, 0, spanV, spanV];
      corners.forEach(([cx, cy, cz], i) => {
        positions.push(cx, cy, cz);
        normals.push(...normal);
        uvs.push(uv.u0 + (uv.u1 - uv.u0) * us[i]!, uv.v0 + (uv.v1 - uv.v0) * vs[i]!);
        colors.push(shade, shade, shade, 1);
      });
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    };
    const w = x1 - x0;
    const d = z1 - z0;
    const h = y1 - y0;
    quad(
      [
        [x0, y1, z0],
        [x1, y1, z0],
        [x1, y1, z1],
        [x0, y1, z1],
      ],
      [0, 1, 0],
      w,
      d,
    );
    quad(
      [
        [x0, y0, z0],
        [x1, y0, z0],
        [x1, y1, z0],
        [x0, y1, z0],
      ],
      [0, 0, -1],
      w,
      h,
    );
    quad(
      [
        [x1, y0, z1],
        [x0, y0, z1],
        [x0, y1, z1],
        [x1, y1, z1],
      ],
      [0, 0, 1],
      w,
      h,
    );
    quad(
      [
        [x0, y0, z1],
        [x0, y0, z0],
        [x0, y1, z0],
        [x0, y1, z1],
      ],
      [-1, 0, 0],
      d,
      h,
    );
    quad(
      [
        [x1, y0, z0],
        [x1, y0, z1],
        [x1, y1, z1],
        [x1, y1, z0],
      ],
      [1, 0, 0],
      d,
      h,
    );
  }
}

/** A merlon's size, in metres: along the wall, across it, and how high it stands. */
const MERLON_WIDTH = 0.42;
const MERLON_DEPTH = 0.26;
const MERLON_HEIGHT = 0.45;

/**
 * Which sides of a tile run on into more of its own ground (bits NORTH, EAST, SOUTH and WEST of
 * `atlasLayout.ts`, north being +z): its top shows an edge only on the others. Past the arena's
 * rim counts as joined, so the world's border draws no verge. Grass joins walls, rocks and trees
 * too: it grows up to them rather than stopping short.
 */
export function joinedSides(map: TileMap, x: number, z: number, family: string): number {
  const scheme = edgeScheme(family);
  if (!scheme) return ALL_SIDES;
  const joinsBlocks = familyOf(family)[scheme]?.joinsBlocks === true;
  const joins = (nx: number, nz: number) => {
    if (!map.inside(nx, nz)) return true;
    const other = map.tile(nx, nz);
    if (other.id === family) return true;
    if (!joinsBlocks) return false;
    const kind = tileShape(other).kind;
    return kind === 'block' || kind === 'prop';
  };
  return (
    (joins(x, z + 1) ? NORTH : 0) |
    (joins(x + 1, z) ? EAST : 0) |
    (joins(x, z - 1) ? SOUTH : 0) |
    (joins(x - 1, z) ? WEST : 0)
  );
}

/** A fixed shade for a tile, 0.9 to 1, from its place: the same arena looks the same every time. */
export function tileShade(x: number, z: number): number {
  let h = Math.imul(x * 73856093, 1) ^ Math.imul(z * 19349663, 1);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return 0.9 + 0.1 * (((h ^ (h >>> 16)) >>> 0) / 4294967295);
}
